import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { ApiError, attachFeedbackScreenshot, getFeedbackInfo, sendFeedback, sendFeedbackJson, type FeedbackInfo } from "../api";
import { t } from "../i18n";
import { FeedbackNotes, RepliedBadge, useFeedbackNotes } from "./FeedbackThreads";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_TEXT = 5000;
const MAX_IMAGE = 5 * 1024 * 1024;

/** The message id of a `#feedback-<id>` anchor (the add_screenshot_url from the chat), or null. */
export function feedbackIdFromHash(hash: string): number | null {
  const m = /^#feedback-(\d+)$/.exec(hash);
  return m ? Number(m[1]) : null;
}

function errorText(e: unknown, attach: boolean): string {
  if (!(e instanceof ApiError)) return t("feedback.errOther");
  if (e.status === 401) return t("feedback.errSignIn");
  if (e.status === 429) return t("feedback.errTooMany");
  if (e.code === "image_too_large") return t("feedback.errTooLarge");
  if (e.code === "invalid_image") return t("feedback.errType");
  if (attach && e.status === 404) return t("feedback.attachNotFound");
  if (attach && e.status === 409) return t("feedback.attachFull");
  return t("feedback.errOther");
}

/** One optional picture: client-side type and size check, a thumbnail through an object URL, «Remove». */
function usePicture() {
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!file) return setUrl(null);
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  function pick(f: File | undefined) {
    if (!f) return;
    if (!TYPES.includes(f.type)) return setError(t("feedback.errType")), void (input.current && (input.current.value = ""));
    if (f.size > MAX_IMAGE) return setError(t("feedback.errTooLarge")), void (input.current && (input.current.value = ""));
    setError(null);
    setFile(f);
  }
  function clear() {
    setFile(null);
    setError(null);
    if (input.current) input.current.value = "";
  }
  return { file, url, error, input, pick, clear };
}

function PicturePicker({ pic, id }: { pic: ReturnType<typeof usePicture>; id: string }) {
  return (
    <div className="field">
      <div className="feedback-pic">
        <input ref={pic.input} id={id} type="file" accept={TYPES.join(",")} onChange={(e) => pic.pick(e.target.files?.[0])} data-testid="feedback-file" />
        <label htmlFor={id} className="btn">
          {t("feedback.addPicture")}
        </label>
        {pic.url && (
          <>
            <img className="feedback-thumb" src={pic.url} alt={t("feedback.preview")} data-testid="feedback-preview" />
            <button type="button" className="btn btn-ghost" onClick={pic.clear} data-testid="feedback-remove">
              {t("feedback.remove")}
            </button>
          </>
        )}
        {!pic.url && <span className="feedback-note">{t("feedback.pictureHint")}</span>}
      </div>
    </div>
  );
}

/** The variant of the form that has a fixed kind and context and no picture (ADR-0062: «Need more than three clients?»). */
export interface FeedbackTopic {
  kind: "bug" | "idea" | "other";
  context?: Record<string, string>;
  title: string;
  lead: string;
  placeholder?: string;
  /** The line after a send, instead of the usual thanks. */
  thanks: string;
  /** Shown under the form once the message is sent. */
  afterSent?: ReactNode;
}

function FeedbackCard({ info, attachId, topic }: { info: FeedbackInfo; attachId: number | null; topic?: FeedbackTopic }) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<"sent" | "attached" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pic = usePicture();
  const notes = useFeedbackNotes(!topic, attachId);
  const max = info.max_text || MAX_TEXT;
  const attach = attachId !== null;

  // The anchor from the chat brings the user to the block.
  const root = useRef<HTMLElement>(null);
  useEffect(() => {
    if (attach) root.current?.scrollIntoView?.();
  }, [attach, attachId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setDone(null);
    setError(null);
    try {
      if (attach) {
        await attachFeedbackScreenshot(attachId, pic.file!);
        setDone("attached");
      } else {
        if (topic) await sendFeedbackJson(text.trim(), topic.kind, topic.context);
        else await sendFeedback(text.trim(), pic.file);
        setText("");
        setDone("sent");
        if (!topic) void notes.reload();
      }
      pic.clear();
    } catch (err) {
      setError(errorText(err, attach));
    } finally {
      setBusy(false);
    }
  }

  function writeNew() {
    setDone(null);
    setError(null);
    pic.clear();
    navigate({ pathname, search, hash: "" }, { replace: true });
  }

  const canSend = !busy && (attach ? pic.file !== null : text.trim().length > 0);
  const message = error ?? pic.error;

  return (
    <section className="card feedback" id="feedback" ref={root} data-testid="feedback-block">
      <h2>
        {topic ? topic.title : t("feedback.title")}
        {!topic && <RepliedBadge show={notes.replied} />}
      </h2>
      <p className="feedback-lead">{topic ? topic.lead : attach ? t("feedback.attachLead") : t("feedback.lead")}</p>
      <form className="feedback-form" onSubmit={submit} noValidate>
        {!attach && (
          <div className="field">
            <label htmlFor="feedback-text">{t("feedback.textLabel")}</label>
            <textarea
              id="feedback-text"
              className="input"
              value={text}
              maxLength={max}
              placeholder={topic?.placeholder ?? t("feedback.placeholder")}
              onChange={(e) => setText(e.target.value)}
              data-testid="feedback-text"
            />
            <div className="feedback-count" data-testid="feedback-counter">
              {t("feedback.counter", { n: text.length, max })}
            </div>
          </div>
        )}
        {!topic && <PicturePicker pic={pic} id="feedback-file" />}
        <div className="feedback-actions">
          <button type="submit" className="btn btn-primary" disabled={!canSend} data-testid="feedback-send">
            {busy ? t("feedback.sending") : attach ? t("feedback.attach") : t("feedback.send")}
          </button>
          {attach && (
            <button type="button" className="btn btn-ghost" onClick={writeNew} data-testid="feedback-write-new">
              {t("feedback.writeNew")}
            </button>
          )}
        </div>
        {message && (
          <div className="msg error" role="alert" data-testid="feedback-error">
            {message}
          </div>
        )}
        {done && (
          <div className="msg info" role="status" data-testid="feedback-done">
            {done === "sent" ? (topic?.thanks ?? t("feedback.thanks")) : t("feedback.attached")}
          </div>
        )}
      </form>
      {done === "sent" && topic?.afterSent}
      {!topic && <FeedbackNotes state={notes} openId={attachId} />}
    </section>
  );
}

/**
 * «Feedback» (ADR-0041): only for a signed-in user and only if the server says the feature is on.
 * Renders nothing until the client knows both - so the pre-rendered HTML (ADR-0036: no user, no effects) never has it.
 * `anchors` turns on the `#feedback-<id>` mode (add a picture to a message from the chat) - /connections only.
 */
export function FeedbackForm({ signedIn, anchors = false, topic }: { signedIn: boolean; anchors?: boolean; topic?: FeedbackTopic }) {
  const [info, setInfo] = useState<FeedbackInfo | null>(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (!signedIn) return setInfo(null);
    let alive = true;
    void getFeedbackInfo().then((r) => alive && setInfo(r));
    return () => {
      alive = false;
    };
  }, [signedIn]);
  if (!signedIn || !info) return null;
  return <FeedbackCard info={info} attachId={anchors && !topic ? feedbackIdFromHash(hash) : null} topic={topic} />;
}
